import React, { useState, useEffect, useRef } from 'react';
import Modal from '../ui/Modal';
import { aiTutorAPI, AskLomiRequest, AskLomiResponse, AIUsageStatus, InteractiveComponent, SubmitAnswerResponse, track } from '../../services/api';
import { useSession } from '../../contexts/SessionContext';
import ChatMCQ from './ChatMCQ';
import ChatFlashcard from './ChatFlashcard';
import ChatTrueFalse from './ChatTrueFalse';
import MarkdownRenderer from './MarkdownRenderer';
import EthiopianFlag from '../EthiopianFlag';
import './AIHelpPanel.css';

export type LearningContext = 'esslce' | 'quiz' | 'content' | 'general';

// Session state for context-aware AI responses
interface SessionState {
  timeOnComponentSeconds: number;
  lastAnswer?: string;
  lastAnswerCorrect?: boolean;
  consecutiveWrong: number;
  questionsAttempted: number;
  questionsCorrect: number;
}

// Initial request for auto-sending (e.g., "Similar Question" feature)
interface InitialRequest {
  question: string;
  source_component?: {
    type: string;
    content: Record<string, any>;
  };
}

interface AIHelpPanelProps {
  context: LearningContext;
  componentType?: string;
  moduleId?: string;
  componentId?: string;
  questionId?: string;
  questionText?: string;
  contentTitle?: string;
  isOpen: boolean;
  onToggle: () => void;
  // Session state for context-aware responses
  sessionState?: SessionState;
  // Optional callback for legacy support
  onAskQuestion?: (question: string) => Promise<string>;
  // Initial request to auto-send when panel opens (for "Similar Question" feature)
  initialRequest?: InitialRequest;
  onInitialRequestSent?: () => void;
  // Aptitude daily drill — sets drill_day_index + drill_strategy_title on
  // the /ai-tutor/ask payload so the prompt builder can mention which
  // path day the student is on. Absent for non-drill surfaces.
  drillContext?: { day_index: number; strategy_title: string };
  // Entry-point surface tag (e.g. "web_past_exam", "web_daily_focus",
  // "web_lesson") — persisted on the conversation for per-surface analytics.
  source?: string;
}

interface ChatMessage {
  role: 'user' | 'ai';
  message: string;
  followUps?: Array<{ label: string; prompt: string; category: string }>;
  interactiveComponent?: InteractiveComponent;
  // Code-switched Amharic translation of `message`, attached to the same
  // bubble so students see the translation right next to the original
  // (not as a fresh bubble at the bottom of the chat).
  amharicTranslation?: string;
}

const AIHelpPanel: React.FC<AIHelpPanelProps> = ({
  context,
  componentType,
  moduleId,
  componentId,
  questionId,
  questionText,
  contentTitle,
  isOpen,
  onToggle,
  sessionState,
  onAskQuestion,
  initialRequest,
  onInitialRequestSent,
  drillContext,
  source,
}) => {
  const [customQuestion, setCustomQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [usage, setUsage] = useState<AIUsageStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [translatingIndex, setTranslatingIndex] = useState<number | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Session tracking for context
  const { trackHelpRequest } = useSession();

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  // Fetch usage status when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchUsage();
    }
  }, [isOpen]);

  // Auto-send initial request when panel opens (for "Similar Question" feature)
  const [initialRequestSent, setInitialRequestSent] = useState(false);

  useEffect(() => {
    if (isOpen && initialRequest && !initialRequestSent && !loading) {
      setInitialRequestSent(true);
      handleAskQuestionWithSource(initialRequest.question, initialRequest.source_component);
      if (onInitialRequestSent) {
        onInitialRequestSent();
      }
    }
    // Reset when panel closes
    if (!isOpen) {
      setInitialRequestSent(false);
    }
  }, [isOpen, initialRequest, initialRequestSent, loading]);

  const fetchUsage = async () => {
    try {
      const usageData = await aiTutorAPI.getUsage();
      setUsage(usageData);
    } catch (err) {
      console.error('Failed to fetch AI usage:', err);
    }
  };

  // Map learning context to API context type
  const getContextType = (): 'module_session' | 'eslce_quiz' | 'dashboard' | 'general' => {
    switch (context) {
      case 'esslce':
        return 'eslce_quiz';
      case 'quiz':
      case 'content':
        return 'module_session';
      default:
        return 'general';
    }
  };

  // Internal function that supports source_component for "Similar Question" feature
  const handleAskQuestionWithSource = async (
    question: string,
    source_component?: { type: string; content: Record<string, any> },
    inputMethod: 'text' | 'voice' = 'text'
  ) => {
    // Check usage limit
    if (usage && !usage.can_send) {
      setError(`Daily message limit reached (${usage.messages_limit}). Upgrade for more!`);
      return;
    }

    setError(null);
    setLoading(true);
    setChatHistory(prev => [...prev, { role: 'user', message: question }]);

    // Track help request for session context
    trackHelpRequest(question, {
      context: context,
      module_id: moduleId,
      component_id: componentId
    });

    try {
      // Use the new API if available, fall back to legacy
      if (moduleId || !onAskQuestion) {
        const request: AskLomiRequest = {
          question,
          context_type: getContextType(),
          module_id: moduleId,
          component_id: componentId,
          question_id: questionId,
          conversation_id: conversationId,
          // Session context for struggle detection and personalization
          time_on_component_seconds: sessionState?.timeOnComponentSeconds,
          last_answer: sessionState?.lastAnswer,
          last_answer_correct: sessionState?.lastAnswerCorrect,
          consecutive_wrong: sessionState?.consecutiveWrong,
          session_questions_attempted: sessionState?.questionsAttempted,
          session_questions_correct: sessionState?.questionsCorrect,
          // For "Similar Question" feature - original component to base generation on
          source_component: source_component,
          // Drill-specific context (Aptitude 45-day path). Backend tolerates absence.
          drill_day_index: drillContext?.day_index,
          drill_strategy_title: drillContext?.strategy_title,
          // Entry-point surface for per-surface analytics.
          source: source,
          // Typed vs. voice (mic transcribe auto-send) origin.
          input_method: inputMethod,
        };

        const response: AskLomiResponse = await aiTutorAPI.ask(request);

        if (response.success) {
          setChatHistory(prev => [...prev, {
            role: 'ai',
            message: response.content,
            followUps: response.follow_up_prompts,
            interactiveComponent: response.interactive_component
          }]);

          // Update conversation ID for continuity
          if (response.conversation_id) {
            setConversationId(response.conversation_id);
          }

          // Update usage
          if (response.usage) {
            setUsage(response.usage);
          }
        } else {
          throw new Error('Failed to get response');
        }
      } else if (onAskQuestion) {
        // Legacy fallback
        const response = await onAskQuestion(question);
        setChatHistory(prev => [...prev, { role: 'ai', message: response }]);
      }
    } catch (err: any) {
      console.error('AI question error:', err);

      // Handle usage limit error
      if (err.response?.status === 429) {
        const detail = err.response?.data?.detail;
        setError(detail?.message || 'Message limit reached. Upgrade for more!');
        setChatHistory(prev => prev.slice(0, -1)); // Remove the user message we just added
      } else {
        setChatHistory(prev => [...prev, {
          role: 'ai',
          message: 'Sorry, I encountered an error. Please try again.'
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Public wrapper without source_component (for regular questions)
  const handleAskQuestion = async (
    question: string,
    inputMethod: 'text' | 'voice' = 'text'
  ) => {
    await handleAskQuestionWithSource(question, undefined, inputMethod);
  };

  const handleQuickQuestion = async (question: string) => {
    // Cohort tag lets us measure whether the new wrong/right/neutral label
    // sets actually pull more engagement vs the old "Common Traps"-era set.
    const cohort =
      sessionState?.lastAnswerCorrect === true ? 'right' :
      sessionState?.lastAnswerCorrect === false ? 'wrong' :
      'neutral';
    track('ask_lomi.quick_question.clicked', {
      context,
      component_type: componentType,
      module_id: moduleId,
      cohort,
      question_label: question.slice(0, 80),
    });
    await handleAskQuestion(question);
  };

  const handleFollowUp = async (prompt: string) => {
    track('ask_lomi.follow_up.clicked', {
      context,
      component_type: componentType,
      module_id: moduleId,
      prompt_label: prompt.slice(0, 80),
    });
    await handleAskQuestion(prompt);
  };

  // Handle interactive component answer submission
  const handleAnswerSubmit = async (
    componentId: string,
    answer: string,
    responseTimeMs?: number
  ): Promise<{
    isCorrect: boolean;
    correctAnswer: string;
    explanation: string;
    followUpPrompts?: Array<{ label: string; prompt: string; category: string }>;
  } | null> => {
    try {
      const response: SubmitAnswerResponse = await aiTutorAPI.submitAnswer({
        component_id: componentId,
        answer: answer,
        conversation_id: conversationId,
        response_time_ms: responseTimeMs,
      });

      if (response.success) {
        return {
          isCorrect: response.is_correct,
          correctAnswer: response.correct_answer,
          explanation: response.explanation || '',
          followUpPrompts: response.follow_up_prompts
        };
      }
      return null;
    } catch (error) {
      console.error('Error submitting answer:', error);
      return null;
    }
  };

  // Voice recording handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks to release the microphone
        stream.getTracks().forEach(track => track.stop());

        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        if (audioBlob.size < 100) return;

        setIsTranscribing(true);
        try {
          const result = await aiTutorAPI.transcribe(audioBlob);
          if (result.success && result.text) {
            setIsTranscribing(false);
            // Check if transcription looks garbled (repeated words, very short nonsense)
            const words = result.text.toLowerCase().split(/\s+/);
            const uniqueWords = new Set(words);
            const isGarbled = words.length >= 2 && uniqueWords.size <= 2;

            if (isGarbled) {
              // Show in input for editing instead of auto-sending
              setCustomQuestion(result.text);
              setError('Voice wasn\'t clear. Edit the text below or try again.');
            } else {
              // Auto-send: transcribe and send immediately (ChatGPT-style).
              // Tag as voice so the trace records its spoken origin.
              await handleAskQuestion(result.text, 'voice');
            }
          } else {
            setError('Could not transcribe audio. Try speaking louder or typing instead.');
            setIsTranscribing(false);
          }
        } catch (err) {
          console.error('Transcription error:', err);
          setError('Voice transcription failed. Please try again.');
          setIsTranscribing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone access denied:', err);
      setError('Microphone access denied. Please allow microphone access to use voice input.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    track('ask_lomi.voice.toggled', {
      context,
      component_type: componentType,
      module_id: moduleId,
      action: isRecording ? 'stop' : 'start',
    });
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Translate the AI bubble at `messageIndex` into Amharic via Gemini and
  // append the translation as a new AI message. We keep the original
  // English message intact so students can compare \u2014 important for
  // technical terms where the Amharic gloss may be unfamiliar.
  const handleTranslateAmharic = async (text: string, messageIndex: number) => {
    if (translatingIndex !== null) return;
    track('ask_lomi.translate_amharic.clicked', {
      surface: 'chat_bubble',
      context,
      component_type: componentType,
      module_id: moduleId,
      message_index: messageIndex,
      char_count: text.length,
    });
    setTranslatingIndex(messageIndex);
    try {
      const result = await aiTutorAPI.translateAmharic(text);
      if (result?.amharic_text) {
        // Attach to the same bubble so the translation lands right next
        // to the original — NOT as a new chat message at the bottom.
        // Otherwise the per-bubble action buttons follow the new bubble
        // and the student loses context on which message was translated.
        setChatHistory(prev =>
          prev.map((m, i) =>
            i === messageIndex
              ? { ...m, amharicTranslation: result.amharic_text }
              : m
          )
        );
      } else {
        setError('Translation came back empty. Try again.');
      }
    } catch (err) {
      console.error('Amharic translation failed:', err);
      setError('Could not translate. Try again.');
    } finally {
      setTranslatingIndex(null);
    }
  };

  const handleCustomQuestion = async () => {
    if (!customQuestion.trim()) return;
    const question = customQuestion;
    track('ask_lomi.message.sent', {
      context,
      component_type: componentType,
      module_id: moduleId,
      char_count: question.length,
    });
    setCustomQuestion('');
    await handleAskQuestion(question);
  };

  const handleClose = () => {
    track('ask_lomi.closed', {
      context,
      component_type: componentType,
      module_id: moduleId,
      messages_in_session: chatHistory.length,
    });
    onToggle();
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCustomQuestion();
    }
  };

  // Quick questions — cohort-aware on past-exam / practice surfaces. Built
  // from prod data: students who got a question wrong rarely want generic
  // "common traps" — they want to know where they went wrong, see a similar
  // one, and have it stated more simply. Right-cohort wants depth instead.
  // Read Aloud removed (0 clicks in 60 days). Amharic now lives as a
  // per-bubble action on AI responses, not a top-level chip.
  const getQuickQuestions = () => {
    // Use componentType for precise matching when available
    // Labels are kept very short (1–2 simple words) — most students read
    // English as a second language. The full prompt sent to the model is
    // separate from the chip label, so prompts stay rich. Avoid idioms
    // ("make it stick", "go deeper") in labels themselves.
    if (componentType === 'eslce_intel') {
      return [
        { icon: '🎯', label: 'More tips', question: 'What other exam strategies should I know for this topic?' },
        { icon: '⚠️', label: 'Try a trap', question: 'Create a question that uses one of these traps so I can practice.' },
      ];
    }
    if (componentType === 'key_definitions') {
      return [
        { icon: '🔄', label: 'Easier', question: 'Explain this in simpler terms.' },
        { icon: '❓', label: 'Quiz me', question: 'Quiz me on these terms to test my understanding.' },
      ];
    }
    if (componentType === 'assessment_set') {
      return [
        { icon: '🔍', label: 'Wrong ones', question: 'Walk me through every question I got wrong on this quiz, one at a time.' },
        { icon: '🎯', label: 'Similar quiz', question: 'Generate a similar practice quiz on the same topic.' },
      ];
    }

    // Past-exam standalone (PastESSLCEQuiz, AptitudeDrill) AND in-module
    // real_eslce_question / practice_mcq → cohort-aware. The wrong cohort
    // already has an auto-prefilled "I answered X but correct is Y" prompt
    // fired in the Mini App; here on web we let them choose.
    const isExamLikeSurface =
      context === 'esslce' ||
      componentType === 'real_eslce_question' ||
      componentType === 'real_esslce_question' ||
      componentType === 'practice_mcq';

    if (isExamLikeSurface) {
      const gotItWrong = sessionState?.lastAnswerCorrect === false;
      const gotItRight = sessionState?.lastAnswerCorrect === true;

      if (gotItWrong) {
        return [
          { icon: '🔍', label: 'My mistake', question: `I answered ${sessionState?.lastAnswer || 'incorrectly'} but it was wrong. Walk me through where my reasoning broke down.` },
          { icon: '🎯', label: 'Try another', question: 'Give me a similar practice question on this concept so I can try again.' },
          { icon: '💡', label: 'Easier', question: 'Explain this in simpler terms with a real-world example.' },
        ];
      }
      if (gotItRight) {
        return [
          { icon: '✅', label: 'Why correct?', question: 'Why is the correct answer right? Go deeper than the stored explanation.' },
          { icon: '🎯', label: 'Try another', question: 'Give me a similar practice question on this concept.' },
          { icon: '🧠', label: 'Memory tip', question: 'Give me a memory trick or pattern so I never miss a question like this again.' },
        ];
      }
      // Hasn't answered yet — neutral set
      return [
        { icon: '✅', label: 'Explain', question: 'Why is the correct answer right? Explain clearly.' },
        { icon: '🎯', label: 'Try another', question: 'Give me a similar practice question on this concept.' },
        { icon: '💡', label: 'Easier', question: 'Explain this in simpler terms with a real-world example.' },
      ];
    }

    // General / dashboard / content fallthrough
    return [
      { icon: '💡', label: 'Help', question: 'What should I focus on studying next?' },
      { icon: '🎯', label: 'Top topics', question: 'What topics are most important for the ESLCE?' },
    ];
  };

  const quickQuestions = getQuickQuestions();

  return (
    <div className="ai-help-panel">
      {/* Floating button to open AI chat */}
      <button
        className="ai-toggle-btn"
        onClick={() => {
          if (!isOpen) {
            track('ask_lomi.opened', {
              context,
              component_type: componentType,
              module_id: moduleId,
              question_id: questionId,
            });
          }
          onToggle();
        }}
      >
        🍋 Ask Lomi
      </button>

      {/* Modal with chat interface */}
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        variant="bottom-sheet"
        title=""
        showCloseButton={false}
        maxHeight="80vh"
        className="ai-chat-modal"
      >
        <div className="ai-modal-content">
          {/* Header */}
          <div className="ai-modal-header">
            <div className="ai-header-left">
              <span className="ai-icon">🍋</span>
              <div>
                <h3>Lomi</h3>
                <p className="ai-subtitle">Your AI study buddy</p>
              </div>
            </div>
            <button className="ai-close-btn" onClick={handleClose}>✕</button>
          </div>

          {/* Error message */}
          {error && (
            <div className="ai-error-banner">
              <span>⚠️ {error}</span>
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}

          {/* Quick Questions - Scrollable single row, hidden once conversation starts */}
          {chatHistory.length === 0 && !loading && (
            <div className="quick-questions-modal">
              {quickQuestions.map((q, index) => (
                <button
                  key={index}
                  className="quick-chip"
                  onClick={() => handleQuickQuestion(q.question)}
                  disabled={loading || !!(usage && !usage.can_send)}
                >
                  <span>{q.icon}</span>
                  <span>{q.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Chat History */}
          <div className="chat-history">
            {chatHistory.length === 0 && !loading && (
              <div className="chat-empty">
                <p>Tap a question above or type your own below</p>
              </div>
            )}
            {chatHistory.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.role}`}>
                {msg.role === 'ai' && <span className="chat-avatar">🍋</span>}
                <div className="chat-content">
                  {/* Text message bubble. Amharic translation (when present)
                      is injected inside the SAME bubble below the English so
                      the action buttons that follow remain anchored to the
                      message they describe. */}
                  {msg.message && (
                    <div className={`chat-bubble ${msg.role === 'ai' ? 'ai-rendered' : ''}`}>
                      {msg.role === 'ai' ? (
                        <MarkdownRenderer>{msg.message}</MarkdownRenderer>
                      ) : (
                        msg.message
                      )}
                      {msg.role === 'ai' && msg.amharicTranslation && (
                        <div className="bubble-amharic">
                          <MarkdownRenderer>{msg.amharicTranslation}</MarkdownRenderer>
                        </div>
                      )}
                      {/* While Gemini is translating THIS bubble, show the
                          LLM-style "thinking" indicator right where the
                          Amharic will land. Visually morphs into the
                          translation when it arrives — and the static
                          "Translating…" button label was leaving students
                          staring at an unmoving chip during the ~10s wait. */}
                      {msg.role === 'ai' && translatingIndex === index && !msg.amharicTranslation && (
                        <div className="bubble-amharic bubble-amharic-loading" role="status" aria-live="polite">
                          <EthiopianFlag size={14} style={{ marginRight: 8, flexShrink: 0 }} />
                          <span className="bubble-amharic-dots" aria-hidden="true">
                            <span></span><span></span><span></span>
                          </span>
                          <span className="bubble-amharic-loading-text">Translating to Amharic…</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Interactive component (MCQ, Flashcard, True/False) */}
                  {msg.role === 'ai' && msg.interactiveComponent && (
                    <div className="interactive-component-wrapper">
                      {msg.interactiveComponent.component_type === 'mcq' && (
                        <ChatMCQ
                          componentId={msg.interactiveComponent.component_id}
                          questionText={msg.interactiveComponent.data.question_text}
                          options={msg.interactiveComponent.data.options.map((o: any) => ({
                            label: o.label,
                            text: o.text
                          }))}
                          correctAnswer={msg.interactiveComponent.data.options.find((o: any) => o.is_correct)?.label || msg.interactiveComponent.data.correct_answer || ''}
                          explanation={msg.interactiveComponent.data.explanation}
                          topic={msg.interactiveComponent.data.topic}
                          difficulty={msg.interactiveComponent.data.difficulty}
                          onAnswerSubmit={handleAnswerSubmit}
                          onFollowUp={handleFollowUp}
                        />
                      )}
                      {msg.interactiveComponent.component_type === 'flashcard' && (
                        <ChatFlashcard
                          front={msg.interactiveComponent.data.front}
                          back={msg.interactiveComponent.data.back}
                          hint={msg.interactiveComponent.data.hint}
                          topic={msg.interactiveComponent.data.topic}
                          onFollowUp={handleFollowUp}
                        />
                      )}
                      {msg.interactiveComponent.component_type === 'true_false' && (
                        <ChatTrueFalse
                          componentId={msg.interactiveComponent.component_id}
                          statement={msg.interactiveComponent.data.statement}
                          correctAnswer={msg.interactiveComponent.data.correct_answer || 'true'}
                          explanation={msg.interactiveComponent.data.explanation}
                          topic={msg.interactiveComponent.data.topic}
                          onAnswerSubmit={handleAnswerSubmit}
                          onFollowUp={handleFollowUp}
                        />
                      )}
                    </div>
                  )}

                  {/* Follow-up prompts from AI (only show if no interactive component) */}
                  {msg.role === 'ai' && !msg.interactiveComponent && msg.followUps && msg.followUps.length > 0 && (
                    <div className="follow-up-prompts">
                      {msg.followUps.map((fu, fuIndex) => (
                        <button
                          key={fuIndex}
                          className="follow-up-chip"
                          onClick={() => handleFollowUp(fu.prompt)}
                          disabled={loading}
                        >
                          {fu.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Per-bubble actions.
                      - Translate: every AI bubble gets one (until translated)
                        so a student can revisit any earlier response, not
                        just the latest.
                      - Shorter: only the latest bubble (it's a follow-up
                        question, not a per-message transformation).
                      Flag is rendered as inline SVG, not the 🇪🇹 emoji,
                      because regional-indicator pairs fall back to literal
                      "ET" on many Linux/Windows fonts and Telegram clients. */}
                  {msg.role === 'ai' && msg.message && (
                    <div className="utility-chips">
                      {!msg.amharicTranslation && (
                        <button
                          className="utility-chip"
                          onClick={() => handleTranslateAmharic(msg.message, index)}
                          disabled={loading || translatingIndex !== null}
                          title="Translate this answer to Amharic"
                        >
                          {translatingIndex === index ? (
                            <>
                              <EthiopianFlag size={14} style={{ marginRight: 4 }} />
                              Translating…
                            </>
                          ) : (
                            <>
                              <EthiopianFlag size={14} style={{ marginRight: 4 }} />
                              በአማርኛ
                            </>
                          )}
                        </button>
                      )}
                      {index === chatHistory.length - 1 && (
                        <button
                          className="utility-chip"
                          onClick={() => handleFollowUp('Make this shorter and simpler. I\'m studying on my phone.')}
                          disabled={loading}
                        >
                          Shorter
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {msg.role === 'user' && <span className="chat-avatar">👤</span>}
              </div>
            ))}
            {loading && (
              <div className="chat-message ai">
                <span className="chat-avatar">🍋</span>
                <div className="chat-bubble loading">
                  <div className="loading-dots">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input area */}
          <div className="chat-input-area">
            <textarea
              placeholder={isRecording
                ? "🔴 Listening... tap mic to send"
                : isTranscribing
                  ? "Sending your voice message..."
                  : usage && !usage.can_send
                    ? "Message limit reached. Upgrade for more!"
                    : "Ask anything..."}
              value={customQuestion}
              onChange={(e) => setCustomQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading || isTranscribing || isRecording || !!(usage && !usage.can_send)}
              rows={1}
            />

            {/* Show mic button when input is empty, send button when text is present */}
            {customQuestion.trim() ? (
              <button
                onClick={handleCustomQuestion}
                disabled={loading || !!(usage && !usage.can_send)}
                className="send-btn"
              >
                ➤
              </button>
            ) : (
              <button
                onClick={toggleRecording}
                disabled={loading || isTranscribing || !!(usage && !usage.can_send)}
                className={`mic-btn ${isRecording ? 'recording' : ''} ${isTranscribing ? 'transcribing' : ''}`}
                title={isRecording ? 'Tap to send' : 'Tap to speak'}
              >
                {isTranscribing ? '⏳' : isRecording ? '⏹' : '🎙'}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AIHelpPanel;
